import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faGear, faRightFromBracket, faBuilding, faUsers, faMicrochip, faGrip, faFileLines, faHeadset, faChartColumn, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../context/AuthContext';
import { ROLE_SUPERADMIN, ROLE_FACILITY_MANAGER, ROLE_NAMES } from '../utils/roles';

const Header = () => {
        const { user, logout } = useContext(AuthContext);
        const navigate = useNavigate();
        const [isDropdownOpen, setIsDropdownOpen] = useState(false);
        const dropdownRef = useRef(null);

        const handleLogout = async () => {
                await logout();
                navigate('/login');
        };

        // Close dropdown when clicking outside
        useEffect(() => {
                const handleClickOutside = (event) => {
                        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                                setIsDropdownOpen(false);
                        }
                };

                document.addEventListener('mousedown', handleClickOutside);
                return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        const menuItems = [
                ...(user?.roleId === ROLE_SUPERADMIN ? [{
                        icon: faBuilding,
                        title: 'Organizations',
                        description: 'Manage customer organizations',
                        link: '/org'
                }] : []),
                ...(user?.roleId === ROLE_SUPERADMIN || user?.roleId === ROLE_FACILITY_MANAGER ? [{
                        icon: faUsers,
                        title: 'Users',
                        description: 'Manage user accounts',
                        link: '/users'
                }] : []),
                ...(user?.roleId === ROLE_FACILITY_MANAGER ? [{
                        icon: faChartColumn,
                        title: 'Reports',
                        description: 'Monthly plant reports',
                        link: '/reports'
                }] : []),
                ...(user?.roleId === ROLE_SUPERADMIN ? [{
                        icon: faMicrochip,
                        title: 'Devices',
                        description: 'View all devices',
                        link: '/devices'
                }] : []),
                ...(user?.roleId === ROLE_SUPERADMIN ? [{
                        icon: faLeaf,
                        title: 'Plants',
                        description: 'Manage plant family care profiles',
                        link: '/plants'
                }] : []),
                ...(user?.roleId === ROLE_SUPERADMIN ? [{
                        icon: faFileLines,
                        title: 'Logs',
                        description: 'View device logs',
                        link: '/logs'
                }] : []),
                ...(user?.roleId === ROLE_SUPERADMIN ? [{
                        icon: faScaleBalanced,
                        title: 'Plant Score',
                        description: 'Weigh alerts in the plant score',
                        link: '/scoring'
                }] : []),
                {
                        icon: faHeadset,
                        title: 'Support',
                        description: 'Get support',
                        link: '/support'
                },
                {
                        icon: faGear,
                        title: 'Settings',
                        description: 'Account and preferences',
                        link: '/settings'
                },
                {
                        icon: faRightFromBracket,
                        title: 'Logout',
                        description: 'Sign out of your account',
                        action: handleLogout
                }
        ];

        return (
                <header id="header" className="px-4 sm:px-8 py-6 bg-white/80 backdrop-blur-sm border-b border-gray-100 relative z-[1000]">
                        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-y-4">
                                <Link to="/">
                                        <img src="/images/logo_text_color.png" alt="CleverPlant" className="h-8" />
                                </Link>
                                <div className="flex items-center space-x-4">
                                        <div className="text-right">
                                                <div data-testid="header-customer" className="text-sm font-semibold text-text-strong">{user ? user.customerName : 'Loading...'}</div>
                                                <div data-testid="header-email" className="text-xs text-text-weak">{user ? user.email : 'Loading...'}</div>
                                                {user?.roleId && (
                                                        <span data-testid="header-role" className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                                user.roleId === ROLE_SUPERADMIN ? 'bg-red-100 text-red-800' :
                                                                user.roleId === ROLE_FACILITY_MANAGER ? 'bg-purple-100 text-purple-800' :
                                                                'bg-green-100 text-green-800'
                                                        }`}>
                                                                {ROLE_NAMES[user.roleId]}
                                                        </span>
                                                )}
                                        </div>
                                        <button
                                                onClick={handleLogout}
                                                data-testid="header-logout"
                                                className="focus-ring w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-xl flex items-center justify-center transition-colors group"
                                                title="Logout"
                                        >
                                                <FontAwesomeIcon icon={faRightFromBracket} className="text-text-weak group-hover:text-red-500" />
                                        </button>
                                        <div className="relative" ref={dropdownRef}>
                                                <button
                                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                        data-testid="header-menu"
                                                        className="focus-ring w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors"
                                                        title="Menu"
                                                >
                                                        <FontAwesomeIcon icon={faGrip} className="text-text-weak" />
                                                </button>
                                                {isDropdownOpen && (
                                                        <div data-testid="header-menu-panel" className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-[9999]">
                                                                {menuItems.map((item, index) => (
                                                                        <div key={index}>
                                                                                {item.link ? (
                                                                                        <Link
                                                                                                to={item.link}
                                                                                                data-testid="header-menu-item"
                                                                                                data-menu-title={item.title}
                                                                                                onClick={() => setIsDropdownOpen(false)}
                                                                                                className="flex items-start px-4 py-3 hover:bg-gray-50 transition-colors"
                                                                                        >
                                                                                                <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                                                                                                        <FontAwesomeIcon icon={item.icon} className="text-text-weak" />
                                                                                                </div>
                                                                                                <div className="flex-1">
                                                                                                        <div className="font-semibold text-text-strong text-sm">{item.title}</div>
                                                                                                        <div className="text-xs text-text-weak mt-0.5">{item.description}</div>
                                                                                                </div>
                                                                                        </Link>
                                                                                ) : (
                                                                                        <button
                                                                                                onClick={() => {
                                                                                                        item.action();
                                                                                                        setIsDropdownOpen(false);
                                                                                                }}
                                                                                                data-testid="header-menu-item"
                                                                                                data-menu-title={item.title}
                                                                                                className="w-full flex items-start px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                                                                                        >
                                                                                                <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                                                                                                        <FontAwesomeIcon icon={item.icon} className="text-text-weak" />
                                                                                                </div>
                                                                                                <div className="flex-1">
                                                                                                        <div className="font-semibold text-text-strong text-sm">{item.title}</div>
                                                                                                        <div className="text-xs text-text-weak mt-0.5">{item.description}</div>
                                                                                                </div>
                                                                                        </button>
                                                                                )}
                                                                        </div>
                                                                ))}
                                                        </div>
                                                )}
                                        </div>
                                </div>
                        </div>
                </header>
        );
};

export default Header;
